import { SortDirection } from '../components/datatable/types/sort-direction.type';
import { TableColumnProp } from '../components/datatable/types/table-column.type';
import type { ValueGetter } from '../components/datatable/utils/column-prop-getters';

export type Comparator = (first: string, second: string) => number;

export abstract class SortingService {

  /**
   * ngx-datatable way of sorting
   * Adapted https://github.com/swimlane/ngx-datatable/blob/master/projects/swimlane/ngx-datatable/src/lib/utils/sort.ts
   */
  static sortRows<T>(rows: Array<T>,
                     prop: string,
                     sortDirection: SortDirection,
                     comparator: Comparator = undefined): Array<T> {
    const compareFunc = comparator !== undefined && typeof comparator === 'function' ?
      comparator
      : SortingService.orderByDefaultComparator;
    return rows.sort((rowA: T, rowB: T): number => {
      const getterFunc =  SortingService.valueGetter(prop);
      const propA = getterFunc(rowA, prop) as string;
      const propB = getterFunc(rowB, prop) as string;
      return sortDirection !== SortDirection.DESC ?
        compareFunc(propA, propB)
        : -compareFunc(propA, propB);
    });
  }

  /**
   * @description  string comparison when empty values are last with ascending order (default nulls are first)
   *
   * @param first
   * @param second
   * @return  A negative number if the reference string occurs before the compare string;
   * positive if the reference string occurs after the compare string; 0 if they are equivalent
   */
  static ascendingNullLastCompare(first: string, second: string): number {
    let result: number;

    const isFirstEmpty: boolean = first === undefined || first == null || first === '';
    const isSecondEmpty: boolean = second === undefined || second == null || second === '';

    if (isFirstEmpty && isSecondEmpty) {
      result = 0;
    } else if (isFirstEmpty) {
      result = 1;
    } else if (isSecondEmpty) {
      result = -1;
    } else {
      result = first.localeCompare(second);
    }

    return result;
  }

  // ngx-datatable adapted comparator from field-ui
  static orderByDefaultComparator(a: unknown, b: unknown): number {
    // Strange logic to assign to 0 for String, but (1) with using valueGetter function, we would never get null or undefined
    a = SortingService.normalizeNilToZero(a);
    b = SortingService.normalizeNilToZero(b);

    // Dates: compare chronologically only when both are Date instances
    if (a instanceof Date && b instanceof Date) {
      return SortingService.compareDates(a, b);
    }

    const aIsNumeric = SortingService.isNumericLike(a);
    const bIsNumeric = SortingService.isNumericLike(b);

    // Both numeric-like: compare numerically; otherwise compare as case-insensitive strings
    if (aIsNumeric && bIsNumeric) {
      return SortingService.compareNumbers(a, b);
    }

    return SortingService.compareStringsCI(a, b);
  }

  // The helpers below are private and preserve legacy semantics used above
  private static normalizeNilToZero(v: unknown): unknown {
    return (v === null || typeof v === 'undefined') ? 0 : v;
  }

  private static isNumericLike(v: unknown): boolean {
    // mirror legacy predicate: numeric-like if parseFloat(...) is a number and global isFinite(v) is true
    // note: global isFinite coerces strings like '123' to number, matching prior behavior
    const parsed = parseFloat(v as string);
    return !isNaN(parsed) && isFinite(v as number);
  }

  private static compareNumbers(a: unknown, b: unknown): number {
    const na = parseFloat(a as string);
    const nb = parseFloat(b as string);
    if (na < nb) { return -1; }
    if (na > nb) { return 1; }
    return 0;
  }

  private static compareStringsCI(a: unknown, b: unknown): number {
    const sa = String(a).toLowerCase();
    const sb = String(b).toLowerCase();
    if (sa < sb) { return -1; }
    if (sa > sb) { return 1; }
    return 0;
  }

  private static compareDates(a: Date, b: Date): number {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  }

  static valueGetter(prop: TableColumnProp): ValueGetter {
    if ((prop as string).indexOf('.') !== -1) {
      return SortingService.deepValueGetter;
    } else {
      return SortingService.shallowValueGetter;
    }
  }

  static deepValueGetter(obj: unknown, path: TableColumnProp): unknown {
    if (obj === null || obj === undefined) {
      return '';
    }
    if (path === null || path === undefined) {
      return obj;
    }

    // check if a path matches a root-level field
    // { "a.b.c": 123 }
    let current = obj[path];
    if (current !== undefined) {
      return current;
    }

    current = obj;
    const split = (path as string).split('.');
    if (split.length !== undefined) {
      for (const i of split) {
        current = current[i];
        // if found undefined, return an empty string
        if (current === undefined || current === null) {
          return '';
        }
      }
    }

    return current;
  }

  static shallowValueGetter(obj: unknown, fieldName: TableColumnProp): unknown {
    if (obj === null || obj === undefined) {
      return '';
    }
    if (fieldName === null || fieldName === undefined) {
      return obj;
    }

    const value = obj[fieldName];
    if (value == null) {
      return '';
    }
    return value;
  }
}
